import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import WebAppService from '@/services/WebAppService';

/* eslint-disable @typescript-eslint/no-explicit-any */

interface SentProject {
  id: string;
  [key: string]: unknown;
}

interface CreateTicketsResponse {
  message?: string;
  project?: {
    project?: {
      id?: string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

interface PollingState {
  // Polling state
  isChecking: boolean;
  startPolling: boolean;
  createTicketsResponse: CreateTicketsResponse | null;
  backoffDelay: number;
  sentProjects: SentProject[];
  noTasksCount: number;
  pollingCompleted: boolean;
  pollingStartedAt: number | null;

  // Actions
  setIsChecking: (isChecking: boolean) => void;
  setStartPolling: (startPolling: boolean) => void;
  setCreateTicketsResponse: (response: CreateTicketsResponse | null) => void;
  setBackoffDelay: (delay: number) => void;
  setSentProjects: (projects: SentProject[]) => void;
  addSentProject: (project: SentProject) => void;
  removeSentProject: (projectId: string) => void;
  clearSentProjects: () => void;
  resetPolling: () => void;
  startPollingTicketsStatus: () => void;
  checkTicketsStatusWithBackoff: (
    refetchProjects: () => void
  ) => Promise<void>;
}

const usePollingStore = create<PollingState>()(
  devtools(
    persist(
      (set, get) => ({
        // Polling state
        isChecking: false,
        startPolling: false,
        createTicketsResponse: null,
        backoffDelay: 1000,
        sentProjects: [],
        noTasksCount: 0,
        pollingCompleted: false,
        pollingStartedAt: null,

        // Actions
        setIsChecking: (isChecking) => set({ isChecking }),
        setStartPolling: (startPolling) => set({ startPolling }),
        setCreateTicketsResponse: (response) =>
          set({
            createTicketsResponse: response,
            pollingCompleted: false,
            pollingStartedAt: response ? Date.now() : null,
          }),
        setBackoffDelay: (delay) => set({ backoffDelay: delay }),
        setSentProjects: (projects) => set({ sentProjects: projects }),
        addSentProject: (project) =>
          set((state) => ({
            sentProjects: [...state.sentProjects, project],
          })),
        removeSentProject: (projectId) =>
          set((state) => ({
            sentProjects: state.sentProjects.filter(
              (project) => project.id !== projectId
            ),
          })),
        clearSentProjects: () => set({ sentProjects: [] }),

        // Reset polling state
        resetPolling: () =>
          set({
            isChecking: false,
            startPolling: false,
            createTicketsResponse: null,
            backoffDelay: 1000,
            sentProjects: [],
            noTasksCount: 0,
            pollingCompleted: false,
            pollingStartedAt: null,
          }),

        // Start polling tickets status
        startPollingTicketsStatus: () => {
          set({ isChecking: true });

          setTimeout(() => {
            const state = get();
            if (
              state.createTicketsResponse?.message ===
              'Tickets already exist for this file'
            ) {
              set({
                startPolling: false,
                sentProjects: [],
                isChecking: false,
              });

              setTimeout(() => {
                set({ createTicketsResponse: null });
              }, 2000);
            } else {
              set({ startPolling: true });
            }
          }, 2000);
        },

        // Check tickets status with backoff
        checkTicketsStatusWithBackoff: async (refetchProjects) => {
          const state = get();

          if (!state.startPolling || !state.isChecking) {
            return;
          }

          // Check if we have the required data after refresh
          if (!state.createTicketsResponse?.project?.project?.id) {
            console.error('Missing createTicketsResponse data after refresh');
            set({
              isChecking: false,
              sentProjects: [],
              startPolling: false,
            });

            // Force immediate persistence of stopped state
            setTimeout(() => {
              // This ensures the persistence layer catches the state change
            }, 0);

            set({
              createTicketsResponse: null,
            });
            return;
          }

          try {
            const response = await WebAppService.getProjectDetails(
              state.createTicketsResponse.project.project.id
            );

            if (response?.data?.length > 0) {
              set({
                isChecking: false,
                sentProjects: [],
                startPolling: false,
                noTasksCount: 0,
                pollingCompleted: true,
              });

              // Force immediate persistence of stopped state
              setTimeout(() => {
                // This ensures the persistence layer catches the state change
              }, 0);

              refetchProjects();
              set({
                createTicketsResponse: null,
              });
            } else {
              set((state) => ({ noTasksCount: state.noTasksCount + 1 }));

              if (get().noTasksCount >= 15) {
                set({
                  isChecking: false,
                  sentProjects: [],
                  startPolling: false,
                  noTasksCount: 0,
                  pollingCompleted: true,
                });

                // Force immediate persistence of stopped state
                setTimeout(() => {
                  // This ensures the persistence layer catches the state change
                }, 0);

              }
            }
          } catch (error) {
            console.error('Error checking tickets status:', error);

            set({
              isChecking: false,
              sentProjects: [],
              startPolling: false,
            });

            // Force immediate persistence of stopped state
            setTimeout(() => {
              // This ensures the persistence layer catches the state change
            }, 0);

            const currentDelay = get().backoffDelay;
            if (currentDelay < 30000) {
              const newDelay = currentDelay * 2;
              set({ backoffDelay: newDelay });

              setTimeout(() => {
                get().checkTicketsStatusWithBackoff(refetchProjects);
              }, newDelay);
            } else {
              set({ startPolling: false });
              console.error('Maximum retry limit reached.');
            }
          }
        },
      }),
      {
        name: 'polling-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          sentProjects: state.sentProjects,
        }),
      }
    ),
    { name: 'PollingStore' }
  )
);

export default usePollingStore;
